// ============================================================
// HOUSE OF CARDS — SceneEngine3D v3 — THE STAGE CAMERA
// ------------------------------------------------------------
// v1 stood the house up. v2 put you inside it and gave you a man
// to walk it with. v3 fixes the lens.
//
// ════════════════════════════════════════════════════════════
// v3 — THE ONE FIX: THE CAMERA IS NOW FIXED.
//
// THE COMPLAINT: too zoomed in, and zooming out slams into an
// invisible wall.
//
// THE CAUSE: v2 fixed the floating diorama by clamping the lens
// INSIDE the four walls. That was the right call — and it made a
// wide shot mathematically impossible. You cannot see a whole
// room from inside it, for the same reason you cannot photograph
// a room while standing with your back flat against its wall.
// The zoom wasn't fighting you. It was fighting the wall.
//
// THE FIX: STOP ORBITING. STAND IN THE DOORWAY.
//
//   · OrbitControls is GONE. No orbit, no zoom, nothing to fight.
//   · The lens sits BEHIND the fourth wall, dead centre, and does
//     not move. Tome walks around inside the frame. That is the
//     2D shot — the same composition, with a real man in it.
//   · The lens is placed by MATH, not by taste: it backs off to
//     exactly the distance where the doorway fills the frame, on
//     ANY aspect ratio, in ANY of the ten rooms. Nothing is ever
//     cropped and no wall is ever seen from behind.
//   · The three stance buttons are now three LENSES, not three
//     orbits: Lean in = long lens, Stand = the shot,
//     Step back = wide. A press is a DOLLY, not a cut.
//   · THE APRON (new): the room's floor, ceiling and walls carry
//     on past the lens, painted near-black. It is what stops the
//     void showing at the top and bottom of the frame — and it
//     vignettes the shot for free. You read it as the dark you're
//     standing in, not as geometry.
//
// KNOBS, all at the STAGE block: STAGE_FIT (how tight the frame
// hugs the doorway), STAGE zoom per stance (Stand / Lean in),
// raise it and the frame drifts with him), STAGE_EASE (dolly
// speed), and hfov/eye/aim per stance.
// ════════════════════════════════════════════════════════════
//
// WHAT CHANGED, AND WHY (all three of caP'n's notes):
//
// 1. IT WAS TOO DARK.  Three reasons, all fixed:
//    · every light value was a first guess and every one was low
//    · the fog was eating the room before the light reached the wall
//    · nothing MOVED the light, so every surface was lit identically
//    Fixed with one tuning block (LIGHT, below — ONE knob: GAIN)
//    and, better, with TOME'S LANTERN. The light walks with him now.
//    Where he stands you can see. Where he isn't is still black.
//    That is not a compromise on the horror. That IS the horror.
//
// 2. IT LOOKED LIKE A DIORAMA FLOATING IN A VOID.  It was. The
//    camera was STANDING OUTSIDE THE BOX: CAM.room sat at z = 6.4
//    and the study's fourth wall is at z = 5.5. You were orbiting a
//    room you were not in, through a wall you couldn't see because
//    a single-sided plane is invisible from behind. Fixed: the
//    camera is now clamped inside the room forever, follows Tome,
//    and the room fills the frame because you are in it.
//
// 3. TOME EXISTS.  See TomeAvatar.js. He walks (WASD / arrows /
//    tap the floor), he carries the lamp, he casts a shadow, and
//    past SPLITTING his shadow stops agreeing with him.
//
// ALSO FIXED (latent, was costing you frames): SpriteBaker fired
//    onBaked() on EVERY render — including when it had baked nothing
//    — which bumped bakeTick, which re-rendered the scene, which
//    re-ran the baker. A render loop. `visible` is memoized now and
//    the baker only bumps the tick when it actually baked something.
//
// THE CONTRACT (ie-3d-conversion §4) — UNBROKEN:
//   SAME 13 PROPS. Identical callbacks. Zero new fields in
//   roomData.js. App.js does not change. Everything is DERIVED.
//   EntityTimer.js untouched.
//
// Y-ELEVATION TABLE (z-fight discipline — never reuse a value):
//   floor              0.000
//   floor grime wash   0.012
//   door threshold     0.020
//   object shadow pad  0.030
//   Tome's shadow      0.038
//   reach ring         0.045
//   walk marker        0.052
//   object base        0.040
// ============================================================
import React, { useRef, useState, useEffect, useMemo, useCallback, Suspense } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Html } from "@react-three/drei";
import * as THREE from "three";

// ⚠️ v4 BUG FIX — THIS LINE WAS MISSING. SceneEngine.js (2D) imports it;
// this file never did. Every nameplate in the 3D build was therefore
// rendering with NO .nameplate / .nameplate-title / .nameplate-desc rules
// at all: no card, no padding, no color — raw serif text inheriting the
// void behind it, then blown up by distanceFactor. That is the "words are
// too dark" bug. It was never a color choice. It was a missing stylesheet.
import "./roomRig.css";

import { RM, veinGlow, ITEM_RARITY, RARITY_COLORS } from "./theme";
import { DOORS, ITEMS } from "./roomData";
import { HOTSPOT_ART, DoorSprite, ITEM_ICONS } from "./HotspotArt";
import { solutionItemsFor } from "./puzzleEngine";
import { eyesVisible, isShadowObject } from "./unstableEyes";
import TomeAvatar, { TOME_R } from "./TomeAvatar";

// ── WORLD CONSTANTS — never hardcode a number below this block ──
const HALL_D = 19.0;    // the hall is a CORRIDOR — it runs deep
const INK = "#33201a";
const TAU = Math.PI * 2;

// ════════════════════════════════════════════════════════════════
// ☀️  THE LIGHT — ONE KNOB. THIS IS THE BRIGHTNESS FIX.
//     Still too dark?  GAIN: 1.3
//     Too bright, lost the dread?  GAIN: 0.75
//     Everything below scales off it. Don't tune the lights
//     individually until GAIN is where you want it.
// ════════════════════════════════════════════════════════════════
const LIGHT = {
  GAIN: 1.0,

  ambient: 0.42,      // was 0.13 — the corners were literally black
  ambientEyes: 0.20,
  hemi: 0.55,         // was 0.22
  hemiEyes: 0.34,

  lampMul: 3.2,       // multiplies each room's own `li`
  lampDecay: 1.35,    // was 1.9 — below 2 the light actually CARRIES
  lampRange: 2.2,     // × room depth (was 1.5)

  key: 0.75,          // was 0.42
  keyEyes: 0.34,
  rim: 0.55,          // was 0.34
  rimEyes: 1.30,

  wallEmis: 0.26,     // the veins catching the lamp (was 0.16)
  sideEmis: 0.18,     // (was 0.10)

  fogMul: 0.62,       // the fog was eating the room. Thinned, not removed.
  exposure: 1.50,     // was 1.15
};
const G = (v) => v * LIGHT.GAIN;

// ── WALKING — the other half of the fix ─────────────────────────
const WALK = {
  SPEED: 2.6,      // units/sec. Tome is 2.9 tall, so ~0.9 body-lengths/sec.
  RUN: 1.7,        // hold Shift
  TURN: 11,        // how fast he pivots toward where he's going
  REACH: 1.9,      // how close he must be to touch a thing
  STANDOFF: 1.35,  // where a click parks him — inside REACH, so he stops short
  SNAP: 0.14,      // "close enough" to a floor click
};
const WALK_KEYS = new Set([
  "w", "a", "s", "d",
  "arrowup", "arrowdown", "arrowleft", "arrowright",
]);

// ── SPRITE SCALE — the hotspots, measured against a 2.9m man ─────
// These were flagged in the roadmap as "chosen blind, before anyone
// could see the result." Now there's a man standing next to them,
// so they have a reference. Nudged up: furniture should read as
// furniture, and the Mouth should be TALLER THAN HIM.
const SPRITE_SCALE = { mouth: 0.034, normal: 0.019, compact: 0.013 };
const BILLBOARD = 0.85; // 0 = planted in the world, 1 = a sticker facing you

// The nameplates are anchored in 3D and shrink with distance. The stage
// lens stands ~2.5× further back than v2's shoulder cam did, so at the
// old value of 9 every label in the house went to gravel. This is the
// same on-screen size as before, from the new distance.
// Labels too big? 16. Too small? 26.
const LABEL_DF = 22;

// ── THE WHITE OUTLINE (v4) ──────────────────────────────────────
// Every word in the world now carries a hard white rim and a black
// halo behind it, WHATEVER color the text itself is. Bone on a dark
// wall, mold-green on a lit floor, flesh-pink on the Mouth — all of
// them stay legible, because the outline is doing the reading work,
// not the fill.
//
// Built out of text-shadow, not -webkit-text-stroke: stroke without
// `paint-order` eats the inside of thin serif strokes (IM Fell is very
// thin), and paint-order on HTML text isn't safe everywhere. Eight
// offset white copies = a true outline, everywhere, no caveats.
//
// Outline too fat? drop the 1px offsets to 0.6px.
// Outline too weak against a bright floor? bump WHITE's alpha to 1.
const LABEL_OUTLINE = (() => {
  const WHITE = "rgba(255,255,255,0.95)";
  const ring = [
    [1, 0], [-1, 0], [0, 1], [0, -1],
    [0.8, 0.8], [-0.8, 0.8], [0.8, -0.8], [-0.8, -0.8],
  ].map(([x, y]) => `${x}px ${y}px 0 ${WHITE}`);
  // a black bed UNDER the white rim, so the rim itself has contrast
  // against a lit floor and doesn't just glow into it.
  return [...ring, "0 0 6px rgba(0,0,0,0.95)", "0 2px 5px rgba(0,0,0,0.85)"].join(", ");
})();

const LABEL_CSS = `
.nameplate-title, .nameplate-desc {
  text-shadow: ${LABEL_OUTLINE};
}
/* the plate itself sits a little heavier in 3D — it's competing with a
   lit room behind it, not a flat 2D backdrop. */
.nameplate {
  background: rgba(9, 6, 5, 0.94);
}
`;

// ════════════════════════════════════════════════════════════════
// 🎥  THE STAGE CAMERA — THE FIX.
//
// v2's lens rode Tome's shoulder and was clamped inside the walls.
// You could not zoom out because there was nowhere to zoom out TO.
//
// v3 stands the lens in the DOORWAY and nails it down. It does not
// orbit. It does not zoom. It does not follow. It stares into the
// room and Tome walks around inside the frame — which is exactly
// what the 2D scene did, except now the furniture has volume, the
// light has a source, and the man casts a shadow.
//
// WHERE THE LENS GOES IS NOT A TASTE CALL — IT'S SOLVED.
// Back it off to the distance where the doorway EXACTLY fills the
// frame. Closer and you crop the room. Further and you see past a
// wall into the void. There is precisely one right answer, it
// depends on the room's width and on the player's aspect ratio, and
// stageShot() computes it every frame. That is why this works in a
// 7.6-wide bathroom and a 19-deep hall, on an ultrawide and on a
// phone, with no per-room tuning anywhere.
//
// The three stances are three LENSES, not three orbits.
// ════════════════════════════════════════════════════════════════
//
// v5 — ONE MARK. ONE LENS. TWO ZOOMS. THE CAMERA NEVER MOVES.
//
// v4 kept three lenses, and each lens re-solved `pull` — so pressing a
// button still DOLLIED the camera to a new spot, and a new spot means a
// new aim point, and a new aim point means "Lean in" could swing the
// whole room out of frame and put you inside the ceiling lamp. That's
// the crop in caP'n's second screenshot. It isn't a bad number. It's
// the whole idea of re-solving the shot per stance.
//
// So it's deleted. There is now exactly ONE camera position per room —
// solved from the BASE lens, the one that used to be "Step back" —
// and the stances do nothing but change the FOV. That is a real zoom
// lens on a locked-off tripod. It CANNOT crop the room in a way the
// base shot doesn't, because it is the base shot, magnified.
//
//   Stand   = the base shot, pulled in a touch. (was "Step back")
//   Lean in = the same shot, magnified — and it AIMS AT TOME, so a long
//             lens can never leave him off the bottom of the frame.
//
// "Step back" is gone. Two buttons.
const BASE_LENS = { hfov: 52 };   // ← the ONLY lens that places the camera.
                                  //   This is v4's "survey" — the one caP'n
                                  //   said was the right shot.

const STAGE = {
  // zoom: magnification off the base shot. 1.0 = the base shot exactly.
  // track: 0 = a dead-still frame. 1 = the aim rides Tome.
  room: { zoom: 1.16, track: 0 },    // STAND. The base shot, tightened a touch —
                                     //   this is the "too much empty space at the
                                     //   bottom" fix. More? 1.24. Less? 1.08.
  lean: { zoom: 1.95, track: 1 },    // LEAN IN. Long lens, locked on him.
                                     //   Tighter? 2.4. Wider? 1.6.
};

// ── THE ONE ANGLE. Both stances look down by exactly this much.
// Want to look down harder? 30. Want it back near eye-level? 16.
const STAGE_TILT = 26;

const STAGE_FIT = 0.95;    // how tightly the BASE frame hugs the doorway.
                           //   1.0 = edge-to-edge (and one bad frame from a void)
                           //   0.95 = 5% of wall in reserve. Don't go above 0.97.
const STAGE_BOOTS = 0.25;  // how far BELOW his boots the bottom of the frame sits when he
                           //   is hard against the near wall — the closest he can ever get
                           //   to the lens. This is what buys the eye height. See below.
const STAGE_AIM_Y = 1.55;  // where a tracking lens looks on him: chest, not crown.
const STAGE_EASE = 0.085;  // a stance press is a ZOOM, not a cut. Higher = snappier.
const FOV_MIN = 14;        // a lens, not a telescope.

// THE SHOT. Given a room, a lens and a screen — where does the camera
// stand, and how high?  Two equations, no taste, no per-room tuning.
//
// 1. HOW FAR BACK.  Back off to exactly the distance at which the
//    doorway (w × h, at z = +d/2) fills the frame:
//
//        tan(halfH) · pull = FIT · w/2   →   pull = FIT·(w/2) / tan(halfH)
//
//    Closer and you crop the room. Further and you see past a side wall.
//    The lens is specified HORIZONTALLY and the vertical FOV is derived
//    from the real aspect ratio — that is what makes `pull`, and so the
//    whole composition, identical on an ultrawide and on a phone.
//
// 2. HOW HIGH.  This is the one that bit: a fixed eye height (say 0.63×h)
//    looks fine in the study and CUTS TOME OFF AT THE KNEES in the hall.
//    A narrow room forces a short pull, a short pull puts him very close
//    to the lens, and up close the bottom of the frame is well above the
//    floor. So don't pick a height — pick a TILT, and let the height fall
//    out of the one thing that actually matters:
//
//        THE FLOOR MUST BE IN FRAME AT THE NEAREST POINT HE CAN STAND.
//
//    He can get to z = d/2 − TOME_R, i.e. Lb = pull + TOME_R away. Demand
//    that the bottom of the frame there sits STAGE_BOOTS below the boards:
//
//        camY − Lb·(tan(tilt) + tan(halfV)) = −BOOTS
//        camY = Lb·(tan(tilt) + tan(halfV)) − BOOTS
//
//    Now his feet are on screen in every room, at every lens, on every
//    screen — and the eye height was never a number anyone had to guess.
//
// v5 — AND IT IS SOLVED EXACTLY ONCE. There is no per-stance geometry
// left. `stageShot` takes a room and a screen, and returns the ONE mark:
// where the tripod stands, how high, what it looks at, and how wide the
// base lens sees. The stances never touch any of it — they only divide
// the FOV. A zoom cannot move a tripod, so a zoom cannot break the shot.

function stageShot(shape, aspect, outPos, outAim) {
  const a = clamp(aspect || 1.6, 0.42, 4.0);

  const wantH = Math.tan((BASE_LENS.hfov * Math.PI) / 180);  // the half-angle we asked for
  let fov = (2 * Math.atan(wantH / a) * 180) / Math.PI;      // → vertical, for THIS screen
  fov = clamp(fov, 30, 84);                                  // a phone in portrait is not a lens
  const tv = Math.tan((fov * Math.PI) / 360);                // half-vertical, as flown
  const th = tv * a;                                         // half-horizontal, as flown

  const pull = clamp((STAGE_FIT * shape.w * 0.5) / th, 1.6, 60);
  const camZ = shape.d * 0.5 + pull;

  const tp = Math.tan((STAGE_TILT * Math.PI) / 180);
  const Lb = pull + TOME_R;                                  // nearest he can ever stand
  // Clamping DOWN is safe — a lower lens only puts MORE floor in frame.
  // Clamping UP at the ceiling is mandatory — never look through the roof.
  const camY = clamp(Lb * (tp + tv) - STAGE_BOOTS, 1.5, shape.h - 0.25);

  // the aim point is just this camera, this tilt, projected into the room.
  const aimZ = -shape.d * 0.05;
  const aimY = camY - (camZ - aimZ) * tp;

  outPos.set(0, camY, camZ);
  outAim.set(0, aimY, aimZ);
  return { fov, pull };
}

const REDUCED =
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// ── mulberry32 — seeded PRNG (verbatim, non-negotiable) ─────────
// The house must look identical on every render, every session,
// every device. Math.random() in a render path = the room reshuffles.
function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const rnd = (seed) => mulberry32((seed * 2654435761) % 2147483647);
// Stable integer seed from any hotspot id string.
const seedOf = (str = "") => {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h % 100000);
};

const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);

// ── THE ROOM STYLE LANGUAGE ─────────────────────────────────────
// Not "room → color." Room → ARCHITECTURE. Height, depth, and the
// shape of the space itself carry the identity. You should know
// which room you're in from the silhouette alone, with the lights
// off. That's the bar (playbook §4a).
const ROOM_SHAPE = {
  //          w      h      d        lamp color        lampY  lampI  fog
  hall:    { w: 8.4,  h: 6.6, d: HALL_D, lamp: RM.flesh,      ly: 5.2, li: 2.4, fog: 0.052 },
  nursery: { w: 9.6,  h: 5.2, d: 10.0,   lamp: RM.moldBright, ly: 4.2, li: 2.0, fog: 0.058 },
  study:   { w: 11.0, h: 6.8, d: 11.0,   lamp: RM.ember,      ly: 5.6, li: 2.6, fog: 0.044 },
  kitchen: { w: 10.2, h: 5.6, d: 10.5,   lamp: RM.mold,       ly: 4.6, li: 2.2, fog: 0.056 },
  bath:    { w: 7.6,  h: 5.4, d: 8.6,    lamp: "#6ea9a0",     ly: 4.4, li: 2.1, fog: 0.078 }, // steam = thick fog
  master:  { w: 12.4, h: 7.4, d: 13.0,   lamp: "#8d9bd6",     ly: 6.2, li: 1.7, fog: 0.040 }, // biggest, coldest
  cellar:  { w: 9.4,  h: 4.4, d: 11.0,   lamp: RM.emberBright,ly: 3.6, li: 2.3, fog: 0.070 }, // low ceiling, presses down
  // Chapter Two — warmer the deeper you go.
  landing:      { w: 8.0, h: 5.8, d: 10.0, lamp: RM.gold,       ly: 4.8, li: 2.3, fog: 0.060 },
  longkitchen:  { w: 9.0, h: 5.4, d: 16.0, lamp: RM.ember,      ly: 4.4, li: 2.4, fog: 0.058 },
  nurserybelow: { w: 9.6, h: 5.2, d: 10.0, lamp: RM.goldBright, ly: 4.2, li: 2.2, fog: 0.062 },
};
const shapeOf = (id) => ROOM_SHAPE[id] || ROOM_SHAPE.hall; // ← fallback: future-proof

// ── THE WALKABLE BOX ────────────────────────────────────────────
// TOME may never leave it. (The CAMERA used to be clamped in here
// too — that was v2's fix for the floating diorama, and it is
// exactly what made a wide shot impossible. v3's lens lives outside
// the box, in the doorway. See THE STAGE CAMERA, below.)
const walkClampX = (shape) => shape.w / 2 - TOME_R;
const walkClampZ = (shape) => shape.d / 2 - TOME_R;

function clampWalk(v, shape) {
  v.x = clamp(v.x, -walkClampX(shape), walkClampX(shape));
  v.z = clamp(v.z, -walkClampZ(shape), walkClampZ(shape));
  v.y = 0;
  return v;
}
// where you're standing when the room opens: just inside the door.
const spawnFor = (shape) => new THREE.Vector3(0, 0, shape.d * 0.18);

// ── PROCEDURAL SURFACE TEXTURES (canvas → CanvasTexture) ────────
// Zero image assets. Every wall, floor and ceiling is drawn at
// runtime. CACHED BY KEY — without this, remounting a room uploads
// a fresh canvas to the GPU every single time.
const TEX_CACHE = new Map();

function paint(key, w, h, draw) {
  if (TEX_CACHE.has(key)) return TEX_CACHE.get(key);
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  const g = c.getContext("2d");
  draw(g, c);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace; // ← or every color comes out pale
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  TEX_CACHE.set(key, t);
  return t;
}

// Rotting wallpaper: base rot, damp blooms, and the VEINS —
// the flesh under the paper, which is the whole thesis of the house.
function wallTexture(roomId) {
  return paint(`wall|${roomId}`, 512, 512, (g, c) => {
    const R = mulberry32(seedOf(roomId) + 11);

    // base plaster
    const grad = g.createLinearGradient(0, 0, 0, c.height);
    grad.addColorStop(0, RM.char2);
    grad.addColorStop(1, RM.char0);
    g.fillStyle = grad;
    g.fillRect(0, 0, c.width, c.height);

    // damp blooms — stains that crawl
    for (let i = 0; i < 46; i++) {
      const x = R() * c.width, y = R() * c.height, r = 12 + R() * 74;
      const bloom = g.createRadialGradient(x, y, 0, x, y, r);
      bloom.addColorStop(0, `rgba(60,40,28,${0.05 + R() * 0.14})`);
      bloom.addColorStop(1, "rgba(0,0,0,0)");
      g.fillStyle = bloom;
      g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill();
    }

    // wallpaper seams (vertical strips of paper)
    g.strokeStyle = "rgba(0,0,0,0.34)";
    g.lineWidth = 2;
    for (let x = 0; x < c.width; x += 86) {
      g.beginPath(); g.moveTo(x, 0); g.lineTo(x, c.height); g.stroke();
    }

    // ── THE VEINS. Capillaries under the paper, branching like a
    // circulatory system. This is what makes the wall ALIVE.
    const vein = (x, y, ang, len, wdt, depth) => {
      if (depth > 4 || len < 5) return;
      const x2 = x + Math.cos(ang) * len;
      const y2 = y + Math.sin(ang) * len;
      g.strokeStyle = `rgba(110,35,51,${0.10 + depth * 0.05})`;
      g.lineWidth = wdt;
      g.lineCap = "round";
      g.beginPath();
      g.moveTo(x, y);
      g.quadraticCurveTo(
        x + Math.cos(ang + 0.5) * len * 0.55,
        y + Math.sin(ang + 0.5) * len * 0.55,
        x2, y2
      );
      g.stroke();
      vein(x2, y2, ang + (R() - 0.5) * 1.25, len * 0.72, wdt * 0.66, depth + 1);
      if (R() > 0.42)
        vein(x2, y2, ang - (R() - 0.5) * 1.25, len * 0.62, wdt * 0.6, depth + 1);
    };
    for (let i = 0; i < 7; i++)
      vein(R() * c.width, R() * c.height, R() * Math.PI * 2, 40 + R() * 40, 3.4, 0);

    // mold at the skirting
    const mold = g.createLinearGradient(0, c.height * 0.78, 0, c.height);
    mold.addColorStop(0, "rgba(0,0,0,0)");
    mold.addColorStop(1, `${RM.moldDim}cc`);
    g.fillStyle = mold;
    g.fillRect(0, c.height * 0.78, c.width, c.height * 0.22);
  });
}

// Floorboards that actually run away from the camera.
function floorTexture(roomId) {
  return paint(`floor|${roomId}`, 512, 512, (g, c) => {
    const R = mulberry32(seedOf(roomId) + 29);
    g.fillStyle = "#1a100a";
    g.fillRect(0, 0, c.width, c.height);

    const PLANK = 46;
    for (let y = 0; y < c.height; y += PLANK) {
      // each plank its own shade — seeded, so it never reshuffles
      const v = 0.55 + R() * 0.45;
      g.fillStyle = `rgb(${Math.floor(52 * v)},${Math.floor(33 * v)},${Math.floor(20 * v)})`;
      g.fillRect(0, y, c.width, PLANK - 2);

      // grain
      g.strokeStyle = "rgba(0,0,0,0.28)";
      g.lineWidth = 1;
      for (let k = 0; k < 5; k++) {
        const gy = y + 5 + R() * (PLANK - 10);
        g.beginPath();
        g.moveTo(0, gy);
        g.bezierCurveTo(c.width * 0.3, gy + (R() - 0.5) * 5, c.width * 0.7, gy + (R() - 0.5) * 5, c.width, gy);
        g.stroke();
      }
      // seam
      g.fillStyle = "rgba(0,0,0,0.62)";
      g.fillRect(0, y + PLANK - 2, c.width, 2);
      // plank end joints
      const jx = R() * c.width;
      g.fillRect(jx, y, 2, PLANK - 2);
    }
    // grime pooling
    for (let i = 0; i < 30; i++) {
      const x = R() * c.width, y = R() * c.height, r = 20 + R() * 80;
      const gr = g.createRadialGradient(x, y, 0, x, y, r);
      gr.addColorStop(0, `rgba(0,0,0,${0.12 + R() * 0.16})`);
      gr.addColorStop(1, "rgba(0,0,0,0)");
      g.fillStyle = gr;
      g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill();
    }
  });
}

function ceilTexture(roomId) {
  return paint(`ceil|${roomId}`, 256, 256, (g, c) => {
    const R = mulberry32(seedOf(roomId) + 53);
    g.fillStyle = "#0c0806";
    g.fillRect(0, 0, c.width, c.height);
    // joists
    for (let x = 0; x < c.width; x += 42) {
      g.fillStyle = "rgba(38,24,16,0.9)";
      g.fillRect(x, 0, 6, c.height);
    }
    // water damage
    for (let i = 0; i < 12; i++) {
      const x = R() * c.width, y = R() * c.height, r = 10 + R() * 40;
      const gr = g.createRadialGradient(x, y, 0, x, y, r);
      gr.addColorStop(0, `rgba(56,38,24,${0.2 + R() * 0.24})`);
      gr.addColorStop(1, "rgba(0,0,0,0)");
      g.fillStyle = gr;
      g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill();
    }
  });
}

// ── SPRITE → TEXTURE — the hand-inked art, preserved exactly ────
// Every HotspotArt sprite becomes a real texture WITHOUT regenerating
// a single line of art. The gothic woodcut IS the material.
//
// HOW (and why it's done this way): we do NOT use react-dom/server.
// `renderToStaticMarkup` pulls in the Node build of react-dom, which
// needs `stream`/`util`, which Webpack 5 and Vite refuse to polyfill.
// It kills the bundle silently. So instead we mount each sprite into
// a real (hidden) DOM node with the React that's already running,
// read the browser's own resolved <svg> back out, and rasterize it.
// Same result. Zero new dependencies. Actually more faithful.
const SPRITE_CACHE = new Map();   // key → { tex, w, h, aspect, ready }
const SPRITE_SS = 3;              // supersample: keep the ink crisp on zoom

// Turn a live <svg> DOM element into a THREE texture.
function rasterizeSvg(svgEl, rec) {
  // Serialize the REAL element — attributes resolved, defs intact.
  let markup = new XMLSerializer().serializeToString(svgEl);
  if (markup.indexOf("xmlns=") === -1) {
    markup = markup.replace("<svg", '<svg xmlns="http://www.w3.org/2000/svg"');
  }

  const vb = (svgEl.getAttribute("viewBox") || "").split(/[\s,]+/).map(Number);
  const sw = parseFloat(svgEl.getAttribute("width")) || (vb[2] || 120);
  const sh = parseFloat(svgEl.getAttribute("height")) || (vb[3] || 120);
  rec.w = sw; rec.h = sh; rec.aspect = sw / sh;

  const blob = new Blob([markup], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const img = new Image();

  img.onload = () => {
    const c = document.createElement("canvas");
    c.width = Math.max(2, Math.round(sw * SPRITE_SS));
    c.height = Math.max(2, Math.round(sh * SPRITE_SS));
    const g = c.getContext("2d");
    g.drawImage(img, 0, 0, c.width, c.height);

    rec.tex.image = c;
    rec.tex.colorSpace = THREE.SRGBColorSpace;  // ← or the ink comes out pale
    rec.tex.minFilter = THREE.LinearMipmapLinearFilter;
    rec.tex.magFilter = THREE.LinearFilter;
    rec.tex.generateMipmaps = true;
    rec.tex.needsUpdate = true;
    rec.ready = true;
    URL.revokeObjectURL(url);
    if (rec.onReady) rec.onReady();
  };
  img.onerror = () => {
    URL.revokeObjectURL(url);
    rec.failed = true;
    if (rec.onReady) rec.onReady();
  };
  img.src = url;
}

// Get (or create) the cache record for a sprite key. Always returns
// a record immediately — the texture fills in when the raster lands.
function spriteRecord(key) {
  let rec = SPRITE_CACHE.get(key);
  if (!rec) {
    // 1x1 transparent placeholder so the material is never null.
    const tex = new THREE.Texture();
    rec = { tex, w: 120, h: 120, aspect: 1, ready: false, failed: false, onReady: null };
    SPRITE_CACHE.set(key, rec);
  }
  return rec;
}

// <SpriteBaker> — mounts every sprite ONCE into a hidden div, off-screen,
// then hands the resulting <svg> to the rasterizer. Runs outside <Canvas>
// (it's plain DOM), costs one hidden node, and never renders again.
function SpriteBaker({ jobs, onBaked }) {
  const hostRef = useRef(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    let pending = 0;
    let done = false;

    const finish = () => {
      if (done) return;
      done = true;
      onBaked && onBaked();
    };

    // Walk the hidden DOM and bake any sprite that hasn't been baked.
    const nodes = host.querySelectorAll("[data-sprite-key]");
    nodes.forEach((node) => {
      const key = node.getAttribute("data-sprite-key");
      const rec = spriteRecord(key);
      if (rec.ready || rec.failed || rec.baking) return;
      const svg = node.querySelector("svg");
      if (!svg) { rec.failed = true; return; }
      rec.baking = true;
      pending++;
      rec.onReady = () => {
        pending--;
        if (pending <= 0) finish();
      };
      rasterizeSvg(svg, rec);
    });

    // ⚠️ THE LOOP FIX. v1 called finish() here unconditionally, which
    // fired onBaked() → setBakeTick() → re-render → new jobs array →
    // this effect again → finish() → … forever, at whatever rate the
    // event loop would give it. If nothing was baked, we do NOTHING.
    if (pending === 0) return;
  }, [jobs, onBaked]);

  return (
    <div
      ref={hostRef}
      aria-hidden
      style={{
        position: "absolute",
        width: 0,
        height: 0,
        overflow: "hidden",
        opacity: 0,
        pointerEvents: "none",
        left: -99999,
        top: -99999,
      }}
    >
      {jobs.map((j) => (
        <div key={j.key} data-sprite-key={j.key}>
          {j.node}
        </div>
      ))}
    </div>
  );
}

// ── COORDINATE MAPPING — the load-bearing derivation ────────────
// roomData gives every hotspot an x,y in PERCENT of a 2D frame.
// We reinterpret that frame as a real room:
//   x → left/right across the room (0% = left wall, 100% = right)
//   y → DEPTH + HEIGHT. In the 2D frame, "higher up" meant "further
//       away." So a low y is FAR and a little raised (things on
//       walls); a high y is NEAR and on the floor. One curve does
//       both, and it's why the old screens still read in 3D.
// The DATA never changed. Only the interpretation of it.
function hotspotWorld(h, shape) {
  // THE MOUTH is the one thing that must be ON the wall, not floating
  // four units in front of it — because now you can WALK BEHIND IT.
  // Derived from `special`, which roomData already had. No new fields.
  if (h.special === "mouth") {
    return [0, shape.h * 0.345, -shape.d / 2 + 0.5];
  }

  const nx = (h.x - 50) / 100;                     // -0.5 .. 0.5
  const ny = Math.min(1, Math.max(0, h.y / 100));  // 0 (far/high) .. 1 (near/low)

  const x = nx * shape.w * 0.86;
  // depth: y=20 → back of the room; y=90 → near the door
  const z = -shape.d * 0.42 + ny * shape.d * 0.72;
  // height: things high in frame hang on the wall; things low sit on the floor
  const lift = Math.pow(1 - ny, 1.7);              // 1 at the top, 0 at the bottom
  const y = 0.04 + lift * shape.h * 0.52;

  return [x, y, z];
}

// The six teeth, mounted in the corridor walls. Shared by the door
// renderer AND the walk rig, so they can never disagree about where
// a door actually is.
function doorWorld(d, shape) {
  const depth = -shape.d * 0.42 + ((d.y - 20) / 70) * shape.d * 0.70;
  const left = d.x < 50;
  const wallX = (left ? -1 : 1) * (shape.w / 2 - 0.06);
  return [wallX, 1.35, depth];
}

// ── THE ROOM SHELL ──────────────────────────────────────────────
function RoomShell({ roomId, shape, onFloor }) {
  const wall = wallTexture(roomId);
  const floor = floorTexture(roomId);
  const ceil = ceilTexture(roomId);

  const wallRepeat = useMemo(() => {
    const t = wall.clone();
    t.repeat.set(shape.w / 5, shape.h / 5);
    t.needsUpdate = true;
    return t;
  }, [wall, shape.w, shape.h]);

  const sideRepeat = useMemo(() => {
    const t = wall.clone();
    t.repeat.set(shape.d / 5, shape.h / 5);
    t.needsUpdate = true;
    return t;
  }, [wall, shape.d, shape.h]);

  const floorRepeat = useMemo(() => {
    const t = floor.clone();
    t.repeat.set(shape.w / 4, shape.d / 4);
    t.needsUpdate = true;
    return t;
  }, [floor, shape.w, shape.d]);

  const ceilRepeat = useMemo(() => {
    const t = ceil.clone();
    t.repeat.set(shape.w / 3, shape.d / 3);
    t.needsUpdate = true;
    return t;
  }, [ceil, shape.w, shape.d]);

  const hy = shape.h / 2;
  const hz = shape.d / 2;
  const hx = shape.w / 2;

  // Every wall is DoubleSide now. The camera lives inside the box, so
  // in theory it never sees a backface — but "in theory" is how you
  // ship a game where one corner of one room is a hole into the void.
  const wallMat = (map, emis) => (
    <meshStandardMaterial
      map={map}
      emissiveMap={map}
      emissive={shape.lamp}
      emissiveIntensity={emis}
      roughness={0.95}
      metalness={0}
      side={THREE.DoubleSide}
    />
  );

  return (
    <group>
      {/* BACK WALL — where the mouth lives */}
      <mesh position={[0, hy, -hz]} receiveShadow>
        <planeGeometry args={[shape.w, shape.h]} />
        {wallMat(wallRepeat, LIGHT.wallEmis)}
      </mesh>

      {/* FOURTH WALL — MOVED. It is not gone; it is now the back of the
          APRON (below), a few metres BEHIND the lens. The room is still
          a closed box. You are simply standing in its doorway looking
          in, which is the only place you can stand and see all of it. */}

      {/* LEFT WALL */}
      <mesh position={[-hx, hy, 0]} rotation={[0, Math.PI / 2, 0]} receiveShadow>
        <planeGeometry args={[shape.d, shape.h]} />
        {wallMat(sideRepeat, LIGHT.sideEmis)}
      </mesh>

      {/* RIGHT WALL */}
      <mesh position={[hx, hy, 0]} rotation={[0, -Math.PI / 2, 0]} receiveShadow>
        <planeGeometry args={[shape.d, shape.h]} />
        {wallMat(sideRepeat, LIGHT.sideEmis)}
      </mesh>

      {/* FLOOR — y 0.00. Also the walk surface: click it, he goes. */}
      <mesh
        position={[0, 0, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
        receiveShadow
        onClick={(e) => { e.stopPropagation(); onFloor && onFloor(e.point); }}
      >
        <planeGeometry args={[shape.w, shape.d]} />
        {/* a little less rough than v1 — the lantern GLINTS off the
            boards as he walks, which is most of what sells the motion */}
        <meshStandardMaterial map={floorRepeat} roughness={0.72} metalness={0.06} />
      </mesh>

      {/* CEILING */}
      <mesh position={[0, shape.h, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <planeGeometry args={[shape.w, shape.d]} />
        <meshStandardMaterial map={ceilRepeat} roughness={1} side={THREE.DoubleSide} />
      </mesh>

      {/* SKIRTING — four boxes. Absurdly cheap, and it is the single
          thing that stops the room reading as five flat planes taped
          together and starts it reading as ARCHITECTURE. */}
      {[
        { k: "b", p: [0, 0.16, -hz + 0.04], r: [0, 0, 0], w: shape.w },
        { k: "f", p: [0, 0.16, hz - 0.04], r: [0, 0, 0], w: shape.w },
        { k: "l", p: [-hx + 0.04, 0.16, 0], r: [0, Math.PI / 2, 0], w: shape.d },
        { k: "r", p: [hx - 0.04, 0.16, 0], r: [0, Math.PI / 2, 0], w: shape.d },
      ].map((s) => (
        <mesh key={s.k} position={s.p} rotation={s.r} receiveShadow>
          <boxGeometry args={[s.w, 0.32, 0.07]} />
          <meshStandardMaterial
            color="#1c130d"
            roughness={0.94}
            emissive={shape.lamp}
            emissiveIntensity={0.06}
          />
        </mesh>
      ))}

      {/* FLOOR GRIME WASH — y 0.012, the room's own color pooling */}
      <mesh position={[0, 0.012, -shape.d * 0.1]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[shape.w * 0.9, shape.d * 0.7]} />
        <meshBasicMaterial color={shape.lamp} transparent opacity={0.05} depthWrite={false} />
      </mesh>
    </group>
  );
}

// ── THE APRON — the dark you are standing in ────────────────────
// The stage lens stands behind the fourth wall, because that is the
// only place a whole room can be seen from. The instant it does,
// three slivers can leak: above the ceiling, below the floor, and —
// on a freak aspect ratio — past a side wall.
//
// The apron closes all three. It is the room's own floor, ceiling and
// walls, carried on PAST the lens and painted near-black, with the
// fourth wall capping the far end so the box is still shut. You never
// read it as geometry. You read it as the dark you're standing in.
// It vignettes the shot for free, and it is the reason the room can
// never float again: there is no angle from which you can see the
// outside of it, because there is no angle.
//
// It sizes ITSELF: long enough to swallow the deepest of the three
// lenses on this screen's aspect ratio, plus a metre of slack.
const SCRATCH_P = new THREE.Vector3();
const SCRATCH_A = new THREE.Vector3();

function useApronLen(shape) {
  const { size } = useThree();
  const aspect = size.width / Math.max(1, size.height);
  return useMemo(() => {
    // v5: one lens, one mark, one number. (Was: the deepest of three.)
    const s = stageShot(shape, aspect, SCRATCH_P, SCRATCH_A);
    return Math.max(3, s.pull) + 1.6;
  }, [shape, aspect]);
}

function Apron({ roomId, shape }) {
  const len = useApronLen(shape);
  const floor = floorTexture(roomId);

  const floorRepeat = useMemo(() => {
    const t = floor.clone();
    t.repeat.set(shape.w / 4, len / 4);
    t.needsUpdate = true;
    return t;
  }, [floor, shape.w, len]);

  const hz = shape.d / 2;
  const cz = hz + len / 2;
  const DARK = "#080605";

  return (
    <group>
      {/* THE BOARDS KEEP RUNNING toward you, out of the light. Same
          texture as the room's floor — but the lamp is behind you now,
          so it simply falls away into black. Nothing to author. */}
      <mesh position={[0, 0, cz]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <planeGeometry args={[shape.w, len]} />
        <meshStandardMaterial map={floorRepeat} color="#4c443b" roughness={0.96} />
      </mesh>

      {/* ceiling */}
      <mesh position={[0, shape.h, cz]} rotation={[Math.PI / 2, 0, 0]}>
        <planeGeometry args={[shape.w, len]} />
        <meshStandardMaterial color={DARK} roughness={1} side={THREE.DoubleSide} />
      </mesh>

      {/* the two sides */}
      <mesh position={[-shape.w / 2, shape.h / 2, cz]} rotation={[0, Math.PI / 2, 0]}>
        <planeGeometry args={[len, shape.h]} />
        <meshStandardMaterial color={DARK} roughness={1} side={THREE.DoubleSide} />
      </mesh>
      <mesh position={[shape.w / 2, shape.h / 2, cz]} rotation={[0, -Math.PI / 2, 0]}>
        <planeGeometry args={[len, shape.h]} />
        <meshStandardMaterial color={DARK} roughness={1} side={THREE.DoubleSide} />
      </mesh>

      {/* THE FOURTH WALL, RELOCATED. Behind the lens. The house is
          still sealed; you are just standing further back in it. */}
      <mesh position={[0, shape.h / 2, hz + len]} rotation={[0, Math.PI, 0]}>
        <planeGeometry args={[shape.w, shape.h]} />
        <meshStandardMaterial color={DARK} roughness={1} side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
}

// ── THE HANGING LAMP, MADE VISIBLE ──────────────────────────────
// v1 had a pointLight up there with nothing to look at. A light with
// no source is a light you don't believe. Now there's a cord, a
// shade, and a bulb that guts with the same rhythm as the light.
function CeilingLamp({ shape, eyesActive }) {
  const bulb = useRef();
  useFrame((st) => {
    if (!bulb.current) return;
    const t = st.clock.elapsedTime;
    const flick = 0.86 + Math.sin(t * 9.1) * 0.05 + Math.sin(t * 23.7) * 0.035;
    bulb.current.material.emissiveIntensity = 2.6 * flick;
  });

  const y = shape.ly;
  const cordLen = Math.max(0.06, shape.h - y - 0.44);

  return (
    <group position={[0, 0, -shape.d * 0.08]}>
      {/* cord */}
      <mesh position={[0, y + 0.44 + cordLen / 2, 0]}>
        <cylinderGeometry args={[0.016, 0.016, cordLen, 5]} />
        <meshStandardMaterial color="#0d0907" roughness={1} />
      </mesh>
      {/* shade */}
      <mesh position={[0, y + 0.22, 0]}>
        <coneGeometry args={[0.52, 0.44, 14, 1, true]} />
        <meshStandardMaterial
          color="#241a13"
          side={THREE.DoubleSide}
          roughness={0.9}
          emissive={shape.lamp}
          emissiveIntensity={0.22}
        />
      </mesh>
      {/* bulb */}
      <mesh ref={bulb} position={[0, y + 0.02, 0]}>
        <sphereGeometry args={[0.12, 10, 8]} />
        <meshStandardMaterial
          color="#0a0705"
          emissive={eyesActive ? "#8b2f7a" : shape.lamp}
          emissiveIntensity={2.6}
          toneMapped={false}
        />
      </mesh>
    </group>
  );
}

// ── DUST — the cheapest motion in the file, and it sells "alive" ─
function Dust({ shape, count = 90, psychosis = 0 }) {
  const ref = useRef();
  const seeds = useMemo(() => {
    const R = mulberry32(1337);
    return Array.from({ length: count }, () => ({
      x: (R() - 0.5) * shape.w * 0.95,
      y: R() * shape.h,
      z: (R() - 0.5) * shape.d * 0.95,
      sp: 0.06 + R() * 0.16,
      ph: R() * Math.PI * 2,
      sz: 0.008 + R() * 0.022,
    }));
  }, [count, shape.w, shape.h, shape.d]);

  const dummy = useMemo(() => new THREE.Object3D(), []);

  useFrame((st) => {
    if (!ref.current || REDUCED) return;
    const t = st.clock.elapsedTime;
    seeds.forEach((s, i) => {
      const y = ((s.y + t * s.sp) % shape.h);
      dummy.position.set(
        s.x + Math.sin(t * 0.3 + s.ph) * 0.28,
        y,
        s.z + Math.cos(t * 0.24 + s.ph) * 0.28
      );
      const p = 1 + psychosis * 0.6;
      dummy.scale.setScalar(s.sz * p);
      dummy.updateMatrix();
      ref.current.setMatrixAt(i, dummy.matrix);
    });
    ref.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh ref={ref} args={[null, null, count]}>
      <sphereGeometry args={[1, 5, 4]} />
      <meshBasicMaterial color={RM.bone} transparent opacity={0.24} depthWrite={false} />
    </instancedMesh>
  );
}

// ── WHERE HE'S GOING — y 0.052 ──────────────────────────────────
function WalkMarker({ walkToRef }) {
  const ref = useRef();
  const t0 = useRef(0);
  const last = useRef(null);

  useFrame((st) => {
    const m = ref.current;
    if (!m) return;
    const w = walkToRef.current;
    if (!w) { m.visible = false; last.current = null; return; }
    if (w !== last.current) { last.current = w; t0.current = st.clock.elapsedTime; }
    m.visible = true;
    m.position.set(w.x, 0.052, w.z);
    const k = ((st.clock.elapsedTime - t0.current) % 1.1) / 1.1;
    m.scale.setScalar(0.55 + k * 0.7);
    m.material.opacity = 0.55 * (1 - k);
  });

  return (
    <mesh ref={ref} rotation={[-Math.PI / 2, 0, 0]} visible={false}>
      <ringGeometry args={[0.34, 0.44, 26]} />
      <meshBasicMaterial
        color={RM.moldBright}
        transparent
        opacity={0.5}
        depthWrite={false}
        toneMapped={false}
      />
    </mesh>
  );
}

// ── A HOTSPOT, STANDING IN THE ROOM ─────────────────────────────
// The sprite hangs on a plane that always faces the camera enough to
// read, but tilts with the world so it has PRESENCE. It floats a hair
// off the floor, breathes, and casts a real contact shadow. And now
// it knows how far away Tome is: it only speaks up when he's close.
function HotspotObject({ target, shape, flags, armedItem, onPick, hovered, setHovered, near, spriteKey, bakeTick }) {
  const spot = target.spot;
  const ref = useRef();
  const ring = useRef();
  const Art = HOTSPOT_ART[spot.id];
  const shadow = isShadowObject(spot);
  const isMouth = spot.special === "mouth";

  // The record exists immediately; the texture fills in when the baker
  // finishes. bakeTick re-renders us the moment it does.
  const rec = useMemo(() => {
    if (!Art) return null;
    const r = spriteRecord(spriteKey);
    return r.failed ? null : r;
  }, [Art, spriteKey, bakeTick]); // eslint-disable-line react-hooks/exhaustive-deps

  const world = target.world;
  const seed = useMemo(() => rnd(seedOf(spot.id) + 3), [spot.id]);
  const bob = useMemo(() => seed(), [seed]);

  // Scale: mouth door is enormous, compact books are small, everything
  // else derives from its own sprite's intrinsic size. Nothing authored.
  const scale = useMemo(() => {
    if (!rec) return [1, 1, 1];
    const base = isMouth
      ? SPRITE_SCALE.mouth
      : spot.compact
      ? SPRITE_SCALE.compact
      : SPRITE_SCALE.normal;
    return [rec.w * base, rec.h * base, 1];
  }, [rec, isMouth, spot.compact]);

  useFrame((st) => {
    if (ring.current && near) {
      const t = st.clock.elapsedTime;
      ring.current.material.opacity = 0.30 + Math.sin(t * 2.6) * 0.16;
    }
    if (!ref.current) return;
    const t = st.clock.elapsedTime;
    // Billboard on Y only — the object turns to face you, but stays
    // planted. Full billboarding makes things feel like stickers.
    ref.current.rotation.y = Math.atan2(
      st.camera.position.x - world[0],
      st.camera.position.z - world[2]
    ) * BILLBOARD;

    if (REDUCED) return;
    // idle breathe — nothing static ever looks alive
    const breathe = Math.sin(t * (isMouth ? 0.9 : 1.5) + bob * 6.28) * (isMouth ? 0.028 : 0.014);
    ref.current.position.y = breathe;
    // the shadow objects breathe HARDER and wrong
    if (shadow) {
      const s = 1 + Math.sin(t * 1.2 + bob * 6.28) * 0.035;
      ref.current.scale.set(scale[0] * s, scale[1] * s, 1);
    }
  });

  // No art, or the bake failed → nothing to stand up.
  // Not baked YET → hold. Otherwise you'd see a blank white plane pop
  // for a frame before the ink lands, which reads as a bug.
  if (!rec || !rec.ready) return null;

  const canUse = spot.useTarget || (spot.actions || []).includes("use");
  const lit = hovered === spot.id;
  const open = lit || near;
  const glowColor = shadow ? "#6a1e50" : isMouth ? RM.fleshGlow : canUse ? RM.moldBright : RM.ember;

  return (
    <group position={world}>
      {/* contact shadow pad — y 0.03. Without this, everything floats. */}
      <mesh position={[0, 0.03 - world[1], 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[scale[0] * 0.5, 20]} />
        <meshBasicMaterial color="#000" transparent opacity={0.45} depthWrite={false} />
      </mesh>

      {/* REACH RING — y 0.045. It lights when Tome is close enough to
          touch the thing. This is the whole grammar of the new game:
          you don't click at a distance, you WALK UP TO IT. */}
      <mesh
        ref={ring}
        position={[0, 0.045 - world[1], 0]}
        rotation={[-Math.PI / 2, 0, 0]}
        visible={!!near}
      >
        <ringGeometry args={[Math.max(0.4, scale[0] * 0.55), Math.max(0.5, scale[0] * 0.55) + 0.1, 26]} />
        <meshBasicMaterial
          color={glowColor}
          transparent
          opacity={0.35}
          depthWrite={false}
          toneMapped={false}
        />
      </mesh>

      <mesh
        ref={ref}
        scale={scale}
        castShadow
        onPointerOver={(e) => { e.stopPropagation(); setHovered(spot.id); document.body.style.cursor = "pointer"; }}
        onPointerOut={() => { setHovered(null); document.body.style.cursor = "auto"; }}
        onClick={(e) => { e.stopPropagation(); onPick(target); }}
      >
        <planeGeometry args={[1, 1]} />
        {/* THE TRICK: map AND emissiveMap from the same texture. The
            ink glows where the sprite is bright — lamps, glints, eyes,
            the mold on a key — and the dark ink stays dark. One
            texture, two jobs, and every object in the house catches
            the light on its own terms. */}
        <meshStandardMaterial
          map={rec.tex}
          emissiveMap={rec.tex}
          emissive={glowColor}
          emissiveIntensity={lit ? 0.9 : near ? 0.5 : isMouth ? 0.42 : 0.2}
          transparent
          alphaTest={0.06}
          side={THREE.DoubleSide}
          roughness={0.8}
          toneMapped={!isMouth}   // the Mouth is allowed to blow past white
          color={shadow ? "#4a2050" : lit || near ? "#ffffff" : "#cfc4b4"}
        />
      </mesh>

      {/* The nameplate — a REAL DOM node, anchored to a 3D point,
          styled with the game's EXISTING CSS. Zero visual drift.
          It goes quiet when Tome is across the room, which is what
          killed the four books shouting over each other in the study. */}
      <Html
        center
        position={[0, -scale[1] * 0.5 - 0.22, 0]}
        style={{
          pointerEvents: "none",
          transition: "opacity 200ms ease",
          opacity: lit ? 1 : near ? 0.95 : 0.62,
        }}
        zIndexRange={[18, 10]}
        distanceFactor={LABEL_DF}
      >
        <div
          className="nameplate plaque"
          style={{
            borderColor: armedItem || canUse ? RM.moldBright : isMouth ? RM.vein : RM.charBorder,
            ...(isMouth ? { boxShadow: veinGlow() } : {}),
            whiteSpace: "nowrap",
          }}
        >
          <div
            className="nameplate-title"
            style={isMouth ? { color: RM.fleshGlow, fontSize: 15 } : undefined}
          >
            {spot.label}
            {canUse && !armedItem ? <span style={S.useTag}> · USE</span> : null}
          </div>
          {open && <div className="nameplate-desc nameplate-desc-open">{spot.desc}</div>}
        </div>
      </Html>

      {/* Interactables get their own small light. The house lights
          only what wants to be touched. */}
      {(canUse || isMouth) && (
        <pointLight
          position={[0, 0.2, 0.5]}
          color={glowColor}
          intensity={G(lit ? 2.6 : near ? 1.6 : isMouth ? 1.6 : 0.6)}
          distance={isMouth ? 7 : 3.2}
          decay={1.8}
        />
      )}
    </group>
  );
}

// ── THE HALL DOORS — mounted on the corridor walls, in real 3D ──
// The six doors no longer sit on a flat frame. They are IN the walls
// of a corridor that runs away from you, and their depth comes
// straight from the y in roomData — 30 far, 55 mid, 80 near.
// Same data. Real corridor. Now you have to WALK to one to open it.
function HallDoor({ target, open, sealed, shape, onPick, hovered, setHovered, near, spriteKey, bakeTick }) {
  const d = target.door;
  const ring = useRef();
  const rec = useMemo(() => {
    const r = spriteRecord(spriteKey);
    return r.failed ? null : r;
  }, [spriteKey, bakeTick]); // eslint-disable-line react-hooks/exhaustive-deps

  const world = target.world;
  const left = d.x < 50;
  const lit = hovered === d.id;

  useFrame((st) => {
    if (ring.current && near) {
      const t = st.clock.elapsedTime;
      ring.current.material.opacity = 0.30 + Math.sin(t * 2.6) * 0.16;
    }
  });

  if (!rec || !rec.ready) return null;
  const H = 2.9;
  const W = H * rec.aspect;
  const ringColor = open ? RM.moldBright : sealed ? RM.charBorder : RM.ember;

  return (
    <group position={[world[0], 0, world[2]]}>
      {/* REACH RING — on the floor at the threshold, y 0.045 */}
      <mesh
        ref={ring}
        position={[left ? 0.4 : -0.4, 0.045, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
        visible={!!near}
      >
        <ringGeometry args={[0.6, 0.72, 26]} />
        <meshBasicMaterial
          color={ringColor}
          transparent
          opacity={0.35}
          depthWrite={false}
          toneMapped={false}
        />
      </mesh>

      <group position={[0, H / 2 - 0.1, 0]} rotation={[0, left ? Math.PI / 2 : -Math.PI / 2, 0]}>
        <mesh
          onPointerOver={(e) => { e.stopPropagation(); setHovered(d.id); document.body.style.cursor = "pointer"; }}
          onPointerOut={() => { setHovered(null); document.body.style.cursor = "auto"; }}
          onClick={(e) => { e.stopPropagation(); onPick(target); }}
          castShadow
        >
          <planeGeometry args={[W, H]} />
          <meshStandardMaterial
            map={rec.tex}
            emissiveMap={rec.tex}
            emissive={open ? RM.moldBright : sealed ? RM.charBorder : RM.emberDim}
            emissiveIntensity={lit ? 0.85 : near ? 0.5 : open ? 0.35 : 0.12}
            transparent
            alphaTest={0.06}
            side={THREE.DoubleSide}
            roughness={0.9}
            color={lit || near ? "#ffffff" : "#cdc2b2"}
          />
        </mesh>

        {/* An UNLOCKED door spills mold-green light into the hall. You
            can see, from the far end of the corridor, exactly how many
            of the six teeth you have opened. That read is the entire
            hall, and it only exists in 3D. */}
        {open && (
          <pointLight
            position={[0, 0, 0.9]}
            color={RM.moldBright}
            intensity={G(lit ? 3.4 : 2.0)}
            distance={6.0}
            decay={1.8}
          />
        )}

        <Html
          center
          position={[0, -H / 2 - 0.3, 0.05]}
          style={{
            pointerEvents: "none",
            transition: "opacity 200ms ease",
            opacity: lit ? 1 : near ? 0.95 : 0.68,
          }}
          zIndexRange={[18, 10]}
          distanceFactor={LABEL_DF}
        >
          <div
            className="nameplate plaque"
            style={{ borderColor: open ? RM.moldDim : RM.charBorder, whiteSpace: "nowrap" }}
          >
            <div className="nameplate-title" style={{ color: open ? RM.moldBright : RM.bone }}>
              {d.label}
            </div>
            <div className="nameplate-desc">
              {open ? "unlocked" : sealed ? "sealed (future wing)" : "locked"}
            </div>
          </div>
        </Html>
      </group>
    </group>
  );
}

// ════════════════════════════════════════════════════════════════
// THE WALK RIG — Tome's legs, and the only thing that owns his
// position. Everything else in the file READS his position; nothing
// else writes it.
//
// Mutates refs, not state. He moves at 60fps; React must never hear
// about it. The ONLY React state this touches is `nearId`, and only
// when he crosses into or out of reach of something — a handful of
// times a minute, not a hundred times a second.
// ════════════════════════════════════════════════════════════════
function TomeRig({
  shape, locked, targets, psychosis, eyesActive,
  tomeRef, facingRef, speedRef, walkToRef, pendingRef,
  onArrive, setNearId,
}) {
  const { camera } = useThree();
  const keys = useRef(new Set());
  const facingGoal = useRef(0);
  const nearRef = useRef(null);

  const FWD = useMemo(() => new THREE.Vector3(), []);
  const RGT = useMemo(() => new THREE.Vector3(), []);

  // ── keyboard. Note what we DON'T touch: E, F, Space, and the mash
  // keys all belong to QTEngine. We only ever claim WASD + arrows. ──
  useEffect(() => {
    const down = (e) => {
      const el = e.target;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable)) return;
      const k = (e.key || "").toLowerCase();
      if (WALK_KEYS.has(k)) {
        keys.current.add(k);
        if (k.startsWith("arrow")) e.preventDefault(); // don't scroll the page
      } else if (k === "shift") {
        keys.current.add("shift");
      }
    };
    const up = (e) => keys.current.delete((e.key || "").toLowerCase());
    const clear = () => keys.current.clear();

    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    window.addEventListener("blur", clear);
    document.addEventListener("visibilitychange", clear);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      window.removeEventListener("blur", clear);
      document.removeEventListener("visibilitychange", clear);
      clear();
    };
  }, []);

  // an overlay opened → drop every held key, or he walks away under it
  useEffect(() => { if (locked) keys.current.clear(); }, [locked]);

  useFrame((st, delta) => {
    const dt = Math.min(0.05, delta || 0.016);
    const pos = tomeRef.current;
    let vx = 0, vz = 0;
    let running = 1;

    if (!locked) {
      const k = keys.current;
      const ax = (k.has("d") || k.has("arrowright") ? 1 : 0) - (k.has("a") || k.has("arrowleft") ? 1 : 0);
      const az = (k.has("w") || k.has("arrowup") ? 1 : 0) - (k.has("s") || k.has("arrowdown") ? 1 : 0);

      if (ax || az) {
        // A key press ALWAYS wins over a click. He's yours.
        walkToRef.current = null;
        pendingRef.current = null;

        // Movement is CAMERA-RELATIVE — "forward" means away from the
        // lens, always, no matter where you've orbited to.
        FWD.set(camera.position.x - pos.x, 0, camera.position.z - pos.z);
        if (FWD.lengthSq() < 1e-4) FWD.set(0, 0, 1);
        FWD.normalize().negate();
        RGT.set(-FWD.z, 0, FWD.x);

        vx = FWD.x * az + RGT.x * ax;
        vz = FWD.z * az + RGT.z * ax;
        const m = Math.hypot(vx, vz) || 1;
        vx /= m; vz /= m;
        running = k.has("shift") ? WALK.RUN : 1;
      } else if (walkToRef.current) {
        const w = walkToRef.current;
        const dx = w.x - pos.x;
        const dz = w.z - pos.z;
        const d = Math.hypot(dx, dz);
        if (d < WALK.SNAP) {
          walkToRef.current = null;
        } else {
          vx = dx / d;
          vz = dz / d;
        }
      }
    }

    // ── integrate, then CLAMP TO THE ROOM. He cannot leave. ────────
    const moving = vx !== 0 || vz !== 0;
    if (moving) {
      pos.x += vx * WALK.SPEED * running * dt;
      pos.z += vz * WALK.SPEED * running * dt;
      facingGoal.current = Math.atan2(-vx, -vz);
    }
    clampWalk(pos, shape);

    // ── turn toward travel, shortest arc ──────────────────────────
    let da = facingGoal.current - facingRef.current;
    while (da > Math.PI) da -= TAU;
    while (da < -Math.PI) da += TAU;
    facingRef.current += da * Math.min(1, WALK.TURN * dt);

    // ── animation speed, smoothed so he eases in and out of a walk ─
    const wantSp = moving ? running : 0;
    speedRef.current += (wantSp - speedRef.current) * Math.min(1, 10 * dt);
    if (speedRef.current < 0.01) speedRef.current = 0;

    // ── DID HE GET THERE? ─────────────────────────────────────────
    // Fires exactly once: pendingRef is cleared BEFORE the callback.
    const pend = pendingRef.current;
    if (pend && !locked) {
      const dx = pend.world[0] - pos.x;
      const dz = pend.world[2] - pos.z;
      if (Math.hypot(dx, dz) <= WALK.REACH) {
        pendingRef.current = null;
        walkToRef.current = null;
        facingGoal.current = Math.atan2(-dx, -dz);   // look at what you came for
        onArrive(pend);
      }
    }

    // ── WHAT'S WITHIN ARM'S LENGTH ────────────────────────────────
    let best = null;
    let bestD = WALK.REACH;
    for (let i = 0; i < targets.length; i++) {
      const t = targets[i];
      const d = Math.hypot(t.world[0] - pos.x, t.world[2] - pos.z);
      if (d < bestD) { bestD = d; best = t.id; }
    }
    if (best !== nearRef.current) {
      nearRef.current = best;
      setNearId(best);
    }
  });

  return (
    <TomeAvatar
      posRef={tomeRef}
      facingRef={facingRef}
      speedRef={speedRef}
      psychosis={psychosis}
      eyesActive={eyesActive}
      gain={LIGHT.GAIN}
    />
  );
}

// ── THE STAGE CAMERA — the whole fix, in 40 lines ───────────────
// No OrbitControls. Nothing to fight. The lens is placed by
// stageShot() and held there. A stance press changes the LENS, and
// the camera dollies to the new mark over ~15 frames. A new room is
// a CUT (flying in from the last room's doorway is a cartoon).
//
// Note what this rig does NOT do: it does not follow him, it does
// not dodge walls, it does not sweep for a clear shot, it cannot
// end up inside his skull, and it can never produce a zero-length
// orbit vector. Every one of those was a v2 problem. They are not
// solved here — they are DELETED. That's the whole reason to do it
// this way.
function FixedCam({ mode, shape, tomeRef, psychosis }) {
  const { camera, size } = useThree();
  const cur = useRef(null);

  const P = useMemo(() => new THREE.Vector3(), []);
  const A = useMemo(() => new THREE.Vector3(), []);
  const L = useMemo(() => new THREE.Vector3(), []);

  // new room → snap. (A stance change deliberately does NOT reset this,
  // so it dollies.)
  useEffect(() => { cur.current = null; }, [shape]);

  useFrame((st) => {
    const preset = STAGE[mode] || STAGE.room;
    const aspect = size.width / Math.max(1, size.height);
    const shot = stageShot(shape, aspect, P, A);   // ← the ONE mark. Stance-blind.

    // THE STANCE IS A ZOOM AND NOTHING ELSE. P is never touched.
    const fovWant = Math.max(FOV_MIN, shot.fov / (preset.zoom || 1));

    // …except that a long lens must LOOK AT HIM, or he walks out of the
    // bottom of a 2× crop and the "camera cuts off" bug is back. track=0
    // (Stand) leaves the frame dead-still; track=1 (Lean in) rides him.
    if (preset.track > 0) {
      const t = tomeRef.current;
      A.x += (t.x - A.x) * preset.track;
      A.y += (STAGE_AIM_Y - A.y) * preset.track;
      A.z += (t.z - A.z) * preset.track;
    }

    if (!cur.current) {
      cur.current = { p: P.clone(), a: A.clone(), fov: fovWant };
    } else {
      const c = cur.current;
      c.p.lerp(P, STAGE_EASE);
      c.a.lerp(A, STAGE_EASE);
      c.fov += (fovWant - c.fov) * STAGE_EASE;
    }

    const c = cur.current;
    camera.position.copy(c.p);
    camera.up.set(0, 1, 0);
    camera.lookAt(L.copy(c.a));

    // ── PSYCHOSIS DRIVES THE CAMERA ─────────────────────────────
    // A slow sick roll and a breathing lens. Applied AFTER lookAt(),
    // which would otherwise wipe it every frame. rotateZ() rolls about
    // the LENS AXIS — set rotation.z directly and the Euler order will
    // betray you the first time the shot has any yaw in it.
    let fov = c.fov;
    if (!REDUCED && psychosis > 0.02) {
      const t = st.clock.elapsedTime;
      const ps = psychosis;
      camera.rotateZ(Math.sin(t * 0.31) * 0.028 * ps + Math.sin(t * 1.7) * 0.006 * ps);
      fov += Math.sin(t * 0.47) * 3.2 * ps;
    }
    if (Math.abs(camera.fov - fov) > 0.005) {
      camera.fov = fov;
      camera.updateProjectionMatrix();
    }
  });

  return null;
}


// ── THE UNSTABLE EYES, IN 3D ────────────────────────────────────
// In 2D the eyes were a CSS filter over a flat frame. In 3D the eyes
// change the LIGHT ITSELF. The bulbs go bruise-violet, the fog
// thickens into the corners, and the veins in the walls stop being
// decoration and start being the only thing you can see.
function EyesLighting({ eyesActive, shape, psychosis }) {
  const key = useRef();
  const rim = useRef();
  const lamp = useRef();

  useFrame((st) => {
    const t = st.clock.elapsedTime;
    if (lamp.current) {
      // the hanging lamp GUTTERS. always. it has been guttering for
      // fifty years and it is not going to stop for you.
      const flick = 0.86 + Math.sin(t * 9.1) * 0.05 + Math.sin(t * 23.7) * 0.035;
      const panic = 1 - psychosis * 0.35;
      const base = G(shape.li * LIGHT.lampMul);
      lamp.current.intensity = base * flick * panic * (eyesActive ? 0.55 : 1);
    }
    if (rim.current && eyesActive) {
      rim.current.intensity = G(LIGHT.rimEyes) * (0.8 + Math.sin(t * 1.4) * 0.3); // the veins PULSE
    }
  });

  return (
    <>
      {/* Ambient is no longer a rounding error. The dark is still a
          feature — but "the corners hold no information" and "the
          player cannot see the floor" are different design goals and
          v1 shipped the second one by accident. */}
      <ambientLight
        intensity={G(eyesActive ? LIGHT.ambientEyes : LIGHT.ambient)}
        color={eyesActive ? "#3a1030" : RM.char3}
      />

      <hemisphereLight
        args={[
          eyesActive ? "#4a1240" : shape.lamp,
          RM.char0,
          G(eyesActive ? LIGHT.hemiEyes : LIGHT.hemi),
        ]}
      />

      {/* THE HANGING LAMP. One warm point above the room, guttering.
          Decay dropped from 1.9 → 1.35: below 2, light actually
          CARRIES across a room instead of dying at arm's length. */}
      <pointLight
        ref={lamp}
        position={[0, shape.ly, -shape.d * 0.08]}
        color={eyesActive ? "#8b2f7a" : shape.lamp}
        intensity={G(shape.li * LIGHT.lampMul)}
        distance={shape.d * LIGHT.lampRange}
        decay={LIGHT.lampDecay}
        castShadow
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
        shadow-bias={-0.0008}
      />

      {/* KEY — rakes across the room from the door side, so every
          object gets a long shadow reaching back into the dark. */}
      <directionalLight
        ref={key}
        position={[shape.w * 0.5, shape.h * 0.9, shape.d * 0.4]}
        intensity={G(eyesActive ? LIGHT.keyEyes : LIGHT.key)}
        color={eyesActive ? "#7a1f60" : RM.emberDim}
        castShadow
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-camera-left={-shape.w}
        shadow-camera-right={shape.w}
        shadow-camera-top={shape.h}
        shadow-camera-bottom={-shape.h}
        shadow-camera-far={shape.d * 2.4}
        shadow-bias={-0.0006}
      />

      {/* THE RIM LIGHT — the cheat code. One cool light from behind,
          low intensity. It puts a rim of color on every silhouette in
          the room and is single-handedly why the house reads as LIT
          rather than merely shaded. It's also what draws the ink line
          around Tome's coat. */}
      <directionalLight
        ref={rim}
        position={[-shape.w * 0.6, shape.h * 0.5, -shape.d * 0.7]}
        intensity={G(eyesActive ? LIGHT.rimEyes : LIGHT.rim)}
        color={eyesActive ? "#9a4fe0" : RM.vein}
      />

      {/* The fog still eats the corners — it's just no longer eating
          the whole room before the light gets to the far wall. */}
      <fogExp2
        attach="fog"
        args={[
          eyesActive ? "#160418" : RM.char0,
          shape.fog * LIGHT.fogMul * (1 + psychosis * 0.55),
        ]}
      />
      <color attach="background" args={[eyesActive ? "#0a0210" : RM.char0]} />
    </>
  );
}

// ── THE PSYCHOSIS BLOOM — a real object in the world ────────────
// A tinted shell around the camera that closes in as Tome breaks.
// At BREAKING the purple is inside the room with you.
function PsychosisShell({ psychosis, eyesActive }) {
  const ref = useRef();
  useFrame((st) => {
    if (!ref.current) return;
    const t = st.clock.elapsedTime;
    ref.current.position.copy(st.camera.position);
    const pulse = 1 + Math.sin(t * 2.1) * 0.03 * psychosis;
    ref.current.scale.setScalar(pulse);
    ref.current.material.opacity = psychosis * 0.30 * (eyesActive ? 1.5 : 1);
  });
  if (psychosis < 0.03) return null;
  return (
    <mesh ref={ref} scale={[1, 1, 1]}>
      <sphereGeometry args={[2.6, 16, 12]} />
      <meshBasicMaterial
        color={RM.psyRamp4}
        transparent
        opacity={0}
        side={THREE.BackSide}
        depthWrite={false}
        toneMapped={false}
        fog={false}
      />
    </mesh>
  );
}

// once per session, not once per room — nobody needs to be told twice
let HINT_SHOWN = false;

// ============================================================
// THE COMPONENT — SAME 13 PROPS AS SceneEngine.js
// ============================================================
export default function SceneEngine3D({
  room,
  state,
  armedItem,
  eyesActive,
  cursed,
  onAction,
  onDoor,
  onChoice,
  onScare,
  onMaybeScare,
  onArm,
  onOverlayChange,
  onLeave,
}) {
  const [closeup, setCloseup] = useState(null);
  const [picker, setPicker] = useState(null);
  const [firedScares, setFiredScares] = useState({});
  const [hovered, setHovered] = useState(null);
  const [camMode, setCamMode] = useState("room");
  const [nearId, setNearId] = useState(null);
  const [hint, setHint] = useState(!HINT_SHOWN);

  const isHall = room.id === "hall";
  const shape = shapeOf(room.id);
  const locked = !!closeup || !!picker;

  // ── TOME'S BODY, IN REFS ──────────────────────────────────────
  // Not state. He moves 60 times a second and React must never hear
  // about it. The rig writes; everything else reads.
  const tomeRef = useRef(spawnFor(shape));
  const facingRef = useRef(0);
  const speedRef = useRef(0);
  const walkToRef = useRef(null);
  const pendingRef = useRef(null);

  // ── drag vs. click. Without this, letting go of an orbit-drag over
  // the floor also tells Tome to walk there, which feels haunted in
  // the wrong way. 8px of travel and we swallow the click.
  const downPt = useRef({ x: 0, y: 0 });
  const dragged = useRef(false);

  // Psychosis 0..1, derived — NOT a new prop. The renderer discovers
  // the state of the mind from the state it was already handed.
  const psychosis = useMemo(() => {
    const p = state.psychosis ?? state.psy ?? state.flags?.psychosis ?? 0;
    return Math.min(1, Math.max(0, p > 1 ? p / 100 : p));
  }, [state.psychosis, state.psy, state.flags]);

  useEffect(() => {
    onOverlayChange && onOverlayChange(locked);
  }, [locked, onOverlayChange]);

  // Walk in through the door, standing up, looking at the room.
  useEffect(() => {
    const s = shapeOf(room.id);
    tomeRef.current.copy(spawnFor(s));
    facingRef.current = 0;
    speedRef.current = 0;
    walkToRef.current = null;
    pendingRef.current = null;
    setCamMode("room");
    setHovered(null);
    setNearId(null);
    setFiredScares({});
  }, [room.id]);

  // fade the control hint out on its own
  useEffect(() => {
    if (!hint) return;
    HINT_SHOWN = true;
    const t = setTimeout(() => setHint(false), 7000);
    return () => clearTimeout(t);
  }, [hint]);

  // ⚠️ MEMOIZED. In v1 this was a fresh array on every render, which
  // churned bakeJobs, which churned SpriteBaker, which called
  // onBaked(), which re-rendered. Round and round. That's fixed here
  // and again inside SpriteBaker — belt and braces.
  const visible = useMemo(
    () =>
      room.hotspots.filter((hs) => {
        if (!eyesVisible(hs, eyesActive)) return false;
        if (hs.hiddenUntil && !state.flags[hs.hiddenUntil]) return false;
        if (hs.removedBy && state.flags[hs.removedBy]) return false;
        return true;
      }),
    [room.hotspots, eyesActive, state.flags]
  );

  // ── EVERYTHING TOME CAN WALK UP TO ─────────────────────────────
  // One list. The renderer, the reach check, and the walk-to all read
  // from it, so they can never disagree about where a thing is.
  const targets = useMemo(() => {
    const list = visible.map((hs) => ({
      id: hs.id,
      kind: "hotspot",
      spot: hs,
      world: hotspotWorld(hs, shape),
    }));
    if (isHall) {
      DOORS.forEach((d) =>
        list.push({ id: d.id, kind: "door", door: d, world: doorWorld(d, shape) })
      );
    }
    return list;
  }, [visible, isHall, shape]);

  // ── THE SPRITE BAKE LIST ────────────────────────────────────────
  // Which hand-inked sprites does THIS room, in THIS state, need? A
  // few sprites react to flags (the crib once rocked, the icebox once
  // opened), so the flag fingerprint goes in the key — that one
  // sprite re-bakes and nothing else does.
  const flagKey = useMemo(() => {
    const f = state.flags || {};
    return [
      f.music_box_opened, f.tub_drained, f.clock_solved,
      f.fireplace_lit, f.took_ice_block, f.all_doors_open,
    ].map((v) => (v ? 1 : 0)).join("");
  }, [state.flags]);

  const bakeJobs = useMemo(() => {
    const jobs = [];
    const seen = new Set();

    visible.forEach((hs) => {
      const Art = HOTSPOT_ART[hs.id];
      if (!Art) return;
      const key = `h|${hs.id}|${flagKey}`;
      if (seen.has(key)) return;
      seen.add(key);
      const rec = SPRITE_CACHE.get(key);
      if (rec && (rec.ready || rec.failed)) return;  // already baked — skip
      jobs.push({ key, node: <Art flags={state.flags} /> });
    });

    if (isHall) {
      DOORS.forEach((d) => {
        const open = state.unlockedDoors.includes(d.id) || d.lockedBy === null;
        const sealed = d.lockedBy === "LOCKED_V1";
        const key = `d|${open ? 1 : 0}|${sealed ? 1 : 0}`;   // only 3 door states exist
        if (seen.has(key)) return;
        seen.add(key);
        const rec = SPRITE_CACHE.get(key);
        if (rec && (rec.ready || rec.failed)) return;
        jobs.push({ key, node: <DoorSprite open={open} sealed={sealed} /> });
      });
    }
    return jobs;
  }, [visible, flagKey, isHall, state.flags, state.unlockedDoors]);

  // Bumped when a batch of sprites finishes rasterizing → the objects
  // that were waiting on a texture re-render and appear.
  const [bakeTick, setBakeTick] = useState(0);
  const onBaked = useCallback(() => setBakeTick((t) => t + 1), []);

  const hotspotKey = (hs) => `h|${hs.id}|${flagKey}`;
  const doorKey = (d) => {
    const open = state.unlockedDoors.includes(d.id) || d.lockedBy === null;
    const sealed = d.lockedBy === "LOCKED_V1";
    return `d|${open ? 1 : 0}|${sealed ? 1 : 0}`;
  };

  // ── IDENTICAL interaction logic to SceneEngine v3. Byte-for-byte
  // the same decision tree. The renderer changed. The game did not.
  const openHotspot = (hs) => {
    const sc = hs.scare;
    if (sc && onScare) {
      const blocked = sc.once && firedScares[hs.id];
      const roll = sc.chance == null ? 1 : Math.random();
      if (!blocked && roll <= (sc.chance == null ? 1 : sc.chance)) {
        if (sc.once) setFiredScares((m) => ({ ...m, [hs.id]: true }));
        onScare();
      }
    }
    if (onMaybeScare && isShadowObject(hs)) onMaybeScare(0.9);
    setCloseup(hs);
  };

  const pickHotspot = (hs) => {
    if (armedItem) return onAction("useItem", hs, armedItem);
    const canUse = hs.useTarget || (hs.actions || []).includes("use");
    const hasCloseup = (hs.actions || []).includes("inspect") || (hs.roomChoices || []).length;
    if (canUse && !hasCloseup) return setPicker(hs);
    if ((hs.actions || []).includes("press")) return onAction("press", hs);
    openHotspot(hs);
  };

  // Fired by the rig the moment his hand can reach the thing. Held in
  // a ref so the rig's useFrame closure never goes stale and we never
  // re-subscribe the frame loop just because armedItem changed.
  const arriveRef = useRef(null);
  arriveRef.current = (t) => {
    if (t.kind === "door") return onDoor(t.door.id);
    return pickHotspot(t.spot);
  };
  const onArrive = useCallback((t) => arriveRef.current && arriveRef.current(t), []);

  // ── CLICK A THING → WALK TO IT → TOUCH IT ─────────────────────
  // If he can already reach it, he touches it now. If he can't, he
  // walks, and the interaction opens the moment he arrives. This is
  // the whole grammar change: the house is a place you're standing
  // in, not a picture you're clicking on.
  const requestTarget = (t) => {
    if (locked || dragged.current) return;
    const pos = tomeRef.current;
    const dx = t.world[0] - pos.x;
    const dz = t.world[2] - pos.z;
    const d = Math.hypot(dx, dz);

    if (d <= WALK.REACH) {
      walkToRef.current = null;
      pendingRef.current = null;
      if (t.kind === "door") onDoor(t.door.id);
      else pickHotspot(t.spot);
      return;
    }
    // stop short, on the line he's already standing on
    const to = new THREE.Vector3(
      t.world[0] - (dx / d) * WALK.STANDOFF,
      0,
      t.world[2] - (dz / d) * WALK.STANDOFF
    );
    walkToRef.current = clampWalk(to, shape);
    pendingRef.current = t;
  };

  const onFloor = (p) => {
    if (locked || dragged.current) return;
    pendingRef.current = null;
    walkToRef.current = clampWalk(new THREE.Vector3(p.x, 0, p.z), shape);
  };

  const closeAll = () => {
    setCloseup(null);
    setPicker(null);
  };

  const CloseupArt = closeup ? HOTSPOT_ART[closeup.id] : null;
  const tint = `rgba(110,35,51,0.22)`;

  return (
    <div
      style={S.frame}
      onPointerDown={(e) => {
        downPt.current = { x: e.clientX, y: e.clientY };
        dragged.current = false;
      }}
      onPointerMove={(e) => {
        if (e.buttons === 0 && e.pointerType === "mouse") return;
        if (Math.hypot(e.clientX - downPt.current.x, e.clientY - downPt.current.y) > 8) {
          dragged.current = true;
        }
      }}
    >
      {/* THE WHITE OUTLINE. Rides on top of roomRig.css (now imported —
          see the top of this file), so every word in the world gets a
          hard white rim no matter what color its fill is. */}
      <style>{LABEL_CSS}</style>

      {/* The hidden sprite oven. Mounts each hand-inked SVG once, off
          screen, hands it to the rasterizer, and never renders again.
          Lives OUTSIDE <Canvas> — it's plain DOM. */}
      <SpriteBaker jobs={bakeJobs} onBaked={onBaked} />

      <Canvas
        shadows
        dpr={[1, 2]}
        camera={{
          // A rough first mark only — FixedCam solves the real one on
          // frame 1 and snaps to it. `far` is generous because the hall
          // is 19 deep and the lens now stands behind its door.
          position: [0, shape.h * 0.63, shape.d / 2 + shape.w * 1.05],
          fov: 52,
          near: 0.08,
          far: 220,
        }}
        gl={{ antialias: true, powerPreference: "high-performance" }}
        onCreated={({ gl }) => {
          gl.toneMapping = THREE.ACESFilmicToneMapping;
          gl.toneMappingExposure = LIGHT.exposure;
        }}
        style={{ position: "absolute", inset: 0, background: RM.char0 }}
      >
        <Suspense fallback={null}>
          <EyesLighting eyesActive={eyesActive} shape={shape} psychosis={psychosis} />
          <RoomShell roomId={room.id} shape={shape} onFloor={onFloor} />
          <Apron roomId={room.id} shape={shape} />
          <CeilingLamp shape={shape} eyesActive={eyesActive} />
          <Dust shape={shape} psychosis={psychosis} />
          <PsychosisShell psychosis={psychosis} eyesActive={eyesActive} />
          <WalkMarker walkToRef={walkToRef} />

          {/* Hall doors — six teeth, down a real corridor */}
          {targets
            .filter((t) => t.kind === "door")
            .map((t) => (
              <HallDoor
                key={t.id}
                target={t}
                open={state.unlockedDoors.includes(t.door.id) || t.door.lockedBy === null}
                sealed={t.door.lockedBy === "LOCKED_V1"}
                shape={shape}
                onPick={requestTarget}
                hovered={hovered}
                setHovered={setHovered}
                near={nearId === t.id}
                spriteKey={doorKey(t.door)}
                bakeTick={bakeTick}
              />
            ))}

          {/* Every hotspot, standing in the room */}
          {targets
            .filter((t) => t.kind === "hotspot")
            .map((t) => (
              <HotspotObject
                key={t.id}
                target={t}
                shape={shape}
                flags={state.flags}
                armedItem={armedItem}
                onPick={requestTarget}
                hovered={hovered}
                setHovered={setHovered}
                near={nearId === t.id}
                spriteKey={hotspotKey(t.spot)}
                bakeTick={bakeTick}
              />
            ))}

          {/* THE MAN HIMSELF */}
          <TomeRig
            shape={shape}
            locked={locked}
            targets={targets}
            psychosis={psychosis}
            eyesActive={eyesActive}
            tomeRef={tomeRef}
            facingRef={facingRef}
            speedRef={speedRef}
            walkToRef={walkToRef}
            pendingRef={pendingRef}
            onArrive={onArrive}
            setNearId={setNearId}
          />

          {/* THE LENS. Fixed. It stands in the doorway and it stays there. */}
          <FixedCam
            mode={camMode}
            shape={shape}
            tomeRef={tomeRef}
            psychosis={psychosis}
          />
        </Suspense>
      </Canvas>

      {/* ── UI CHROME — plain React, outside the Canvas. Everything
          below this line is the game's existing interface, untouched. */}

      {/* THE THREE LENSES. Not three orbits — three focal lengths on
          one fixed mark. A press dollies; it never cuts. */}
      <div style={S.camBar}>
        {[
          ["room", "Stand"],   // the base shot. (v4's "Step back", tightened.)
          ["lean", "Lean in"], // the same shot, magnified, locked on him.
        ].map(([m, label]) => (
          <button
            key={m}
            className="hotspot-card"
            style={{
              ...S.camBtn,
              borderColor: camMode === m ? RM.moldBright : RM.charBorder,
              color: camMode === m ? RM.moldBright : RM.boneDim,
            }}
            onClick={() => setCamMode(m)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Leave room */}
      {!isHall && (
        <div onClick={onLeave} style={S.leaveBtn} className="hotspot-card">
          ← back to the Hall
        </div>
      )}

      {/* How to move. Says it once, then never again. */}
      {hint && !locked && (
        <div style={S.hint}>
          walk with <b style={S.hintKey}>W A S D</b> · or tap the floor · hold{" "}
          <b style={S.hintKey}>SHIFT</b> to run
        </div>
      )}

      {/* Inspect close-up — IDENTICAL to SceneEngine v3 */}
      {closeup && (
        <div style={S.closeupVeil} onClick={closeAll}>
          <div style={S.closeupCard} className="plaque" onClick={(e) => e.stopPropagation()}>
            {closeup.art ? (
              <img src={closeup.art} alt="" style={S.plateImg}
                onError={(e) => (e.target.style.display = "none")} />
            ) : (
              <div
                style={{
                  ...S.plateArt,
                  background: `radial-gradient(ellipse at 50% 42%, ${tint} 0%, transparent 72%), linear-gradient(180deg, #181009 0%, #0a0705 100%)`,
                }}
              >
                {CloseupArt ? (
                  <div style={S.plateSpriteWrap}>
                    <CloseupArt flags={state.flags} />
                  </div>
                ) : (
                  <>
                    <div style={S.plateMonogram}>{closeup.label.charAt(0)}</div>
                    <div style={S.plateHint}>· the house remembers what this looks like ·</div>
                  </>
                )}
              </div>
            )}
            <div style={S.closeupTitle}>{closeup.label}</div>
            <div style={S.closeupBody}>{closeup.closeup || closeup.desc}</div>

            {(() => {
              const choices = (closeup.roomChoices || []).filter((c) => {
                if (c.requiresFlag && !state.flags[c.requiresFlag]) return false;
                if (c.once && state.flags[c.once]) return false;
                return true;
              });
              if (!choices.length) return null;
              return (
                <div style={S.choiceWrap}>
                  {choices.map((c) => (
                    <button
                      key={c.id}
                      style={S.choiceBtn}
                      onClick={() => { onChoice(c.id); closeAll(); }}
                    >
                      <span style={S.choiceLabel}>{c.label}</span>
                      {c.hint && <span style={S.choiceHint}>{c.hint}</span>}
                    </button>
                  ))}
                </div>
              );
            })()}

            <div style={S.closeupActions}>
              {closeup.actions.includes("take") && (
                <button
                  style={S.btn}
                  onClick={() => { onAction("take", closeup); closeAll(); }}
                >
                  {closeup.takeLabel || "Take"}
                </button>
              )}
              {(closeup.useTarget || (closeup.actions || []).includes("use")) && (
                <button
                  style={S.btn}
                  onClick={() => { const target = closeup; setCloseup(null); setPicker(target); }}
                >
                  Use…
                </button>
              )}
              <button style={S.btnDim} onClick={closeAll}>Step back</button>
            </div>
          </div>
        </div>
      )}

      {/* Pocket picker — IDENTICAL to SceneEngine v3 */}
      {picker && (
        <PocketPicker
          hotspot={picker}
          inventory={state.inventory || []}
          cursed={cursed}
          solutions={cursed ? null : solutionItemsFor(picker.id, state)}
          onPick={(itemId) => {
            const target = picker;
            closeAll();
            onAction("useItem", target, itemId);
          }}
          onClose={closeAll}
        />
      )}
    </div>
  );
}

// ── Pocket picker overlay — unchanged from SceneEngine v3 ────────
function PocketPicker({ hotspot, inventory, cursed, solutions, onPick, onClose }) {
  const monogram = (name = "?") =>
    name.split(" ").map((w) => w[0]).slice(0, 2).join("");
  return (
    <div style={S.closeupVeil} onClick={onClose}>
      <div style={S.pickerCard} className="plaque" onClick={(e) => e.stopPropagation()}>
        <div style={S.pickerTitle}>Use what on {hotspot.label}?</div>
        {inventory.length === 0 ? (
          <div style={S.pickerEmpty}>Your pockets are empty. The house notices empty hands.</div>
        ) : (
          <div style={S.pickerGrid}>
            {inventory.map((id) => {
              const it = ITEMS[id];
              const rarity = RARITY_COLORS[ITEM_RARITY[id]] || RM.boneDim;
              const Icon = ITEM_ICONS[id];
              const lit = cursed || (solutions && solutions.includes(id));
              const dim = !cursed && !lit;
              return (
                <button
                  key={id}
                  title={it?.desc}
                  onClick={() => onPick(id)}
                  className="hotspot-card plaque"
                  style={{
                    ...S.pickerItem,
                    borderColor: lit ? RM.moldBright : rarity,
                    opacity: dim ? 0.34 : 1,
                    filter: dim ? "grayscale(0.7)" : "none",
                    boxShadow: lit ? veinGlow(RM.moldBright) : `inset 0 0 12px rgba(0,0,0,0.6)`,
                  }}
                >
                  <div style={{ ...S.pickerSlot, color: rarity, borderColor: `${rarity}55` }}>
                    {Icon ? <Icon /> : monogram(it?.name)}
                  </div>
                  <div style={{ ...S.pickerItemName, color: lit ? RM.moldBright : RM.bone }}>
                    {it?.name || id}
                  </div>
                </button>
              );
            })}
          </div>
        )}
        <button style={S.btnDim} onClick={onClose}>Step back</button>
      </div>
    </div>
  );
}

const S = {
  frame: { position: "absolute", inset: 0, overflow: "hidden", touchAction: "none" },

  camBar: {
    position: "absolute",
    right: 18,
    top: 14,
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-end",
    gap: 8,
    maxWidth: "58vw",
    zIndex: 30,
  },
  camBtn: {
    padding: "7px 13px",
    background: "rgba(13,9,7,0.9)",
    border: `1px solid ${INK}`,
    borderRadius: 8,
    fontFamily: RM.fontD,
    fontSize: 12,
    letterSpacing: 1,
    cursor: "pointer",
    transition: "all 140ms ease",
  },

  hint: {
    position: "absolute",
    left: "50%",
    bottom: 22,
    transform: "translateX(-50%)",
    padding: "8px 16px",
    background: "rgba(7,5,4,0.78)",
    border: `1px solid ${INK}`,
    borderRadius: 999,
    fontFamily: RM.font,
    fontStyle: "italic",
    fontSize: 13,
    color: RM.boneFaint,
    letterSpacing: 0.5,
    whiteSpace: "nowrap",
    pointerEvents: "none",
    zIndex: 28,
    animation: "menubreath 5s ease-in-out infinite",
  },
  hintKey: {
    fontFamily: RM.fontD,
    fontStyle: "normal",
    color: RM.boneDim,
    letterSpacing: 1.5,
  },

  useTag: { fontFamily: RM.fontD, fontSize: 11, letterSpacing: 2, color: RM.moldBright },

  leaveBtn: {
    position: "absolute",
    left: 18,
    top: 14,
    padding: "8px 14px",
    background: "rgba(13,9,7,0.9)",
    border: `1px solid ${RM.charBorder}`,
    borderRadius: 8,
    fontFamily: RM.fontD,
    fontSize: 13,
    color: RM.boneDim,
    cursor: "pointer",
    zIndex: 30,
  },
  closeupVeil: {
    position: "absolute",
    inset: 0,
    background: "rgba(5,3,2,0.82)",
    backdropFilter: "blur(2px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 45,
  },
  closeupCard: {
    width: "min(460px, 90vw)",
    maxHeight: "88vh",
    overflowY: "auto",
    padding: "22px 26px 26px",
    background: "linear-gradient(180deg, #1a110c 0%, #100a07 100%)",
    border: `1px solid ${RM.vein}`,
    borderRadius: 14,
    boxShadow: `${veinGlow()}, 0 30px 80px rgba(0,0,0,0.7)`,
  },
  plateArt: {
    minHeight: 130,
    padding: "14px 0",
    borderRadius: 9,
    border: `1px solid ${RM.charBorder}`,
    marginBottom: 16,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    boxShadow: "inset 0 0 40px rgba(0,0,0,0.6)",
  },
  plateSpriteWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    filter: "drop-shadow(0 8px 16px rgba(0,0,0,0.6))",
  },
  plateImg: {
    width: "100%",
    height: 160,
    objectFit: "cover",
    borderRadius: 9,
    border: `1px solid ${RM.charBorder}`,
    marginBottom: 16,
  },
  plateMonogram: {
    fontFamily: RM.fontT,
    fontWeight: 900,
    fontSize: 44,
    color: "rgba(216,203,179,0.16)",
    lineHeight: 1,
  },
  plateHint: {
    fontFamily: RM.font,
    fontStyle: "italic",
    fontSize: 10,
    letterSpacing: 2,
    color: RM.boneFaint,
  },
  closeupTitle: {
    fontFamily: RM.fontT,
    fontWeight: 700,
    fontSize: 20,
    letterSpacing: 3,
    textTransform: "uppercase",
    color: RM.bone,
    marginBottom: 12,
    borderBottom: `1px solid ${RM.charBorder}`,
    paddingBottom: 8,
  },
  closeupBody: {
    fontFamily: RM.font,
    fontSize: 16,
    color: RM.boneDim,
    lineHeight: 1.55,
    whiteSpace: "pre-wrap",
  },
  closeupActions: { display: "flex", gap: 10, marginTop: 18, flexWrap: "wrap" },
  pickerCard: {
    width: "min(440px, 90vw)",
    maxHeight: "84vh",
    overflowY: "auto",
    padding: "22px 24px 24px",
    background: "linear-gradient(180deg, #1a110c 0%, #100a07 100%)",
    border: `1px solid ${RM.moldDim}`,
    borderRadius: 14,
    boxShadow: `${veinGlow(RM.moldBright)}, 0 30px 80px rgba(0,0,0,0.7)`,
  },
  pickerTitle: {
    fontFamily: RM.fontD,
    fontSize: 18,
    letterSpacing: 2,
    color: RM.bone,
    marginBottom: 16,
    borderBottom: `1px solid ${RM.charBorder}`,
    paddingBottom: 10,
  },
  pickerEmpty: {
    fontFamily: RM.font,
    fontStyle: "italic",
    fontSize: 14,
    color: RM.boneFaint,
    marginBottom: 16,
  },
  pickerGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
    gap: 10,
    marginBottom: 16,
  },
  pickerItem: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    padding: "8px 12px 8px 8px",
    border: "1px solid",
    borderRadius: 9,
    background: "linear-gradient(180deg, #1a110c, #100a07)",
    fontFamily: RM.font,
    fontSize: 14,
    cursor: "pointer",
    textAlign: "left",
    transition: "all 120ms ease",
  },
  pickerSlot: {
    width: 34,
    height: 34,
    flex: "0 0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid",
    borderRadius: 7,
    background: "rgba(0,0,0,0.45)",
    fontFamily: RM.fontT,
    fontWeight: 700,
    fontSize: 13,
    letterSpacing: 1,
  },
  pickerItemName: { lineHeight: 1.1 },
  choiceWrap: { display: "flex", flexDirection: "column", gap: 8, marginTop: 16 },
  choiceBtn: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    padding: "10px 14px",
    background: "linear-gradient(180deg, #20140d, #140c08)",
    border: `1px solid ${RM.vein}`,
    borderRadius: 9,
    color: RM.bone,
    fontFamily: RM.font,
    fontSize: 15,
    cursor: "pointer",
    textAlign: "left",
  },
  choiceLabel: { color: RM.bone },
  choiceHint: {
    fontFamily: RM.fontD,
    fontStyle: "italic",
    fontSize: 12,
    color: RM.boneFaint,
    letterSpacing: 1,
  },
  btn: {
    padding: "9px 18px",
    background: RM.moldDim,
    border: `1px solid ${RM.moldBright}`,
    borderRadius: 8,
    color: RM.bone,
    fontFamily: RM.fontD,
    fontSize: 14,
    cursor: "pointer",
  },
  btnDim: {
    padding: "9px 18px",
    background: "transparent",
    border: `1px solid ${RM.charBorder}`,
    borderRadius: 8,
    color: RM.boneDim,
    fontFamily: RM.fontD,
    fontSize: 14,
    cursor: "pointer",
  },
};
